import React from 'react';
import { SafeImg } from '../../SafeImg';
import { E } from './_shared';

/* ════════════════════════════════════════════════════════════════════════
 * EVENTS — new variants (Iter 189c Phase B)
 *   • agenda-list     · vertical timeline with date badges + agenda text
 *   • featured-hero   · large hero event + 3 compact side cards
 * The section title / eyebrow / subtitle and every item's date / title /
 * location / description are inline-editable.
 * ════════════════════════════════════════════════════════════════════════ */
export function EventsExt({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const layout = block.layout;
  const items = Array.isArray(c.items) ? c.items : [];

  // ── agenda-list ───────────────────────────────────────────────────────
  if (layout === 'agenda-list') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || 'default'} className="px-6 py-24" style={{ background: theme.bg || '#FAF6EE', color: theme.ink || '#0E0E23' }}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <E block={block} onEdit={onEdit} field="eyebrow" value={c.eyebrow || 'AGENDA'} tag="p" className="text-xs uppercase tracking-[0.24em] font-bold" style={{ color: theme.accent || '#8B5CF6' }} placeholder="Eyebrow" />
            <E block={block} onEdit={onEdit} field="title" value={c.title || "What's on the schedule"} tag="h2" className="mt-3 text-4xl md:text-5xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
            {c.subtitle && <E block={block} onEdit={onEdit} field="subtitle" value={c.subtitle} tag="p" className="mt-3 opacity-70" placeholder="Subtitle" />}
          </div>
          <div className="divide-y" style={{ borderColor: `${theme.ink || '#0E0E23'}15` }}>
            {items.map((ev, i) => (
              <div key={i} className="flex items-center gap-6 py-6 border-t" style={{ borderColor: `${theme.ink || '#0E0E23'}15` }}>
                <div
                  className="flex-shrink-0 text-center font-bold px-4 py-3 rounded-lg"
                  style={{ background: theme.accent || '#8B5CF6', color: '#fff', minWidth: 80, fontFamily: theme.headingFont }}
                >
                  <E block={block} onEdit={onEdit} field={`items.${i}.date`} value={ev.date || '10:00'} tag="span" className="text-lg tracking-wider" placeholder="Time" />
                </div>
                <div className="flex-1">
                  <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={ev.title || 'Session Title'} tag="p" className="text-lg font-bold leading-tight" style={{ fontFamily: theme.headingFont }} placeholder="Session title" />
                  <E block={block} onEdit={onEdit} field={`items.${i}.location`} value={ev.location || 'Room'} tag="p" className="mt-1 text-sm opacity-60 uppercase tracking-widest" placeholder="Location" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── featured-hero ─────────────────────────────────────────────────────
  if (layout === 'featured-hero') {
    const hero = items[0] || {};
    const side = items.slice(1, 4);
    return (
      <section data-block-type={block.type} data-layout={block.layout || 'default'} className="px-6 py-24" style={{ background: theme.bg || '#FAF6EE', color: theme.ink || '#0E0E23' }}>
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <E block={block} onEdit={onEdit} field="eyebrow" value={c.eyebrow || 'UPCOMING'} tag="p" className="text-xs uppercase tracking-[0.24em] font-bold" style={{ color: theme.accent || '#8B5CF6' }} placeholder="Eyebrow" />
            <E block={block} onEdit={onEdit} field="title" value={c.title || 'Events worth blocking your calendar for'} tag="h2" className="mt-3 text-4xl md:text-5xl font-bold leading-tight" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {/* Hero card */}
            <div className="md:col-span-8 overflow-hidden relative" style={{ borderRadius: theme.radius || 8, minHeight: 420, background: '#0E0E23' }}>
              {hero.image
                ? <SafeImg src={hero.image} alt={hero.title} className="absolute inset-0 w-full h-full object-cover opacity-80" />
                : <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${theme.accent || '#8B5CF6'}, #EC4899)` }} />
              }
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-8 text-white">
                <E block={block} onEdit={onEdit} field="items.0.date" value={hero.date || 'JUN 07'} tag="p" className="text-xs uppercase tracking-[0.24em] font-bold" style={{ color: theme.accent || '#8B5CF6' }} placeholder="Date" />
                <E block={block} onEdit={onEdit} field="items.0.title" value={hero.title || 'Featured Event'} tag="h3" className="mt-2 text-3xl md:text-4xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Title" />
                <E block={block} onEdit={onEdit} field="items.0.location" value={hero.location || 'Location · City'} tag="p" className="mt-2 opacity-80" placeholder="Location" />
                {hero.description && <E block={block} onEdit={onEdit} field="items.0.description" value={hero.description} tag="p" className="mt-3 max-w-lg opacity-80 text-sm leading-relaxed" placeholder="Description" />}
              </div>
            </div>
            {/* Side stack */}
            <div className="md:col-span-4 flex flex-col gap-4">
              {side.map((ev, i) => (
                <div key={i} className="flex-1 overflow-hidden relative" style={{ borderRadius: theme.radius || 8, minHeight: 130, background: '#F1F5F9' }}>
                  {ev.image && <SafeImg src={ev.image} alt={ev.title} className="absolute inset-0 w-full h-full object-cover" />}
                  <div className="absolute inset-0 bg-black/40" />
                  <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                    <E block={block} onEdit={onEdit} field={`items.${i + 1}.date`} value={ev.date || 'DATE'} tag="p" className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: theme.accent || '#8B5CF6' }} placeholder="Date" />
                    <E block={block} onEdit={onEdit} field={`items.${i + 1}.title`} value={ev.title || 'Event'} tag="h4" className="mt-1 text-base font-bold leading-tight" style={{ fontFamily: theme.headingFont }} placeholder="Title" />
                    <E block={block} onEdit={onEdit} field={`items.${i + 1}.location`} value={ev.location || 'Location'} tag="p" className="mt-1 text-xs opacity-70" placeholder="Location" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return null;
}
